import { useEffect, useState } from 'react'
import { Plus, X } from 'lucide-react'
import { api } from '@/lib/api'

interface Tag {
  id: string
  name: string
  color: string | null
  group: { id: string; name: string; exclusiveInGroup: boolean } | null
}

interface ClientTag {
  id: string
  tag: Tag
  appliedBy: 'USER' | 'RULE' | 'WORKFLOW' | 'AI'
  appliedAt: string
}

interface TagPickerProps {
  clientStubId: string
  refreshSignal?: number
}

export function TagPicker({ clientStubId, refreshSignal = 0 }: TagPickerProps) {
  const [applied, setApplied] = useState<ClientTag[]>([])
  const [catalog, setCatalog] = useState<Tag[]>([])
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')

  async function load() {
    try {
      const [a, c] = await Promise.all([
        api<{ items: ClientTag[] }>(`/v1/marketing/tags/clients/${clientStubId}`),
        api<{ items: Tag[] }>(`/v1/marketing/tags`),
      ])
      setApplied(a.items)
      setCatalog(c.items)
    } catch {
      // ignorar; el padre muestra errores generales
    }
  }

  useEffect(() => {
    void load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientStubId, refreshSignal])

  async function apply(tagId: string) {
    await api(`/v1/marketing/tags/clients/${clientStubId}/apply`, {
      method: 'POST',
      body: JSON.stringify({ tagId, appliedBy: 'USER' }),
    })
    setOpen(false)
    setQ('')
    await load()
  }

  async function remove(tagId: string) {
    await api(`/v1/marketing/tags/clients/${clientStubId}/apply/${tagId}`, { method: 'DELETE' })
    await load()
  }

  const appliedIds = new Set(applied.map((a) => a.tag.id))
  const candidates = catalog
    .filter((t) => !appliedIds.has(t.id))
    .filter((t) => !q || t.name.toLowerCase().includes(q.toLowerCase()))

  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-wider text-ink-700/60 mb-1.5">
        tags
      </div>
      <div className="flex flex-wrap gap-1">
        {applied.map((ct) => (
          <span
            key={ct.id}
            className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded border"
            style={{
              borderColor: ct.tag.color ?? '#cbd5e1',
              color: ct.tag.color ?? '#475569',
            }}
            title={`${ct.appliedBy.toLowerCase()} · ${new Date(ct.appliedAt).toLocaleDateString('es-ES')}`}
          >
            {ct.tag.name}
            <button
              onClick={() => void remove(ct.tag.id)}
              className="hover:text-red-600"
              type="button"
              aria-label={`quitar ${ct.tag.name}`}
            >
              <X className="size-3" />
            </button>
          </span>
        ))}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded border border-dashed border-ink-900/20 text-ink-700/60 hover:bg-surface-alt"
        >
          <Plus className="size-3" />
          {applied.length === 0 ? 'añadir tag' : 'añadir'}
        </button>
      </div>
      {open && (
        <div className="mt-2 bp-card p-2">
          <input
            autoFocus
            placeholder="buscar…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-full border border-ink-900/15 rounded px-2 py-1 text-xs"
          />
          <ul className="mt-1 max-h-40 overflow-auto">
            {candidates.map((t) => (
              <li key={t.id}>
                <button
                  type="button"
                  onClick={() => void apply(t.id)}
                  className="w-full text-left px-2 py-1 text-xs hover:bg-surface-alt rounded flex items-center gap-2"
                >
                  <span
                    className="size-2 rounded-full"
                    style={{ background: t.color ?? '#cbd5e1' }}
                  />
                  <span>{t.name}</span>
                  {t.group && (
                    <span className="ml-auto font-mono text-[10px] text-ink-700/40">
                      {t.group.name}
                    </span>
                  )}
                </button>
              </li>
            ))}
            {candidates.length === 0 && (
              <li className="px-2 py-2 text-xs text-ink-700/60 text-center">
                sin coincidencias
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  )
}
